import {
    Row,
    Col,
    Table,
    Input,
    Button,
    Modal,
    Space,
    Spin,
    List,
    Avatar,
    Typography,
    InputRef,
    TableColumnType,
    Card,
    message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createOrg, deleteOrg, listOrgs } from "../../common/api/OrganizationClient";
import { impersonateOrgBot, listOrgUsers } from "../../common/api/UserClient";

import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { Organization } from "../../types/OrganizationTypes";
import { OrganizationModal } from "./OrganizationModal";

const { Column } = Table;

export const OrganizationView: React.FC = () => {
    const [data, setData]: any[] = useState([]);
    const [loading, setLoading]: any[] = useState(true);
    const searchInput = useRef<InputRef>(null);
    const [showModal, setShowModal] = useState(false);
    const { refreshMe } = useUserState();

    const createOrgClick = async (org: Organization) => {
        try {
            await createOrg(org.name);
            await refreshOrgs();
            message.success("Organization created successfully");
            setShowModal(false);
            return true;
        } catch (error) {
            handleError(error);
            return false;
        }
    };

    const refreshOrgs = async () => {
        setLoading(true);
        try {
            const data = await listOrgs();
            setData(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refreshOrgs();
    }, []);

    const handleImpersonate = async (org: Organization) => {
        try {
            const res = await impersonateOrgBot(org.id);
            refreshMe();
            return res;
        } catch (error) {
            handleError(error);
        }
    };

    const handleDelete = async (org: Organization) => {
        const modal = Modal.confirm({
            content: <Spin spinning />,
        });

        const users = await listOrgUsers(org.id);
        modal.update({
            title: "Are you sure to delete this organization?",
            width: 500,
            content: (
                <>
                    <Typography.Text strong>Organization Name: {org.name}</Typography.Text>
                    <br />
                    <List
                        size="small"
                        header="Users"
                        dataSource={users}
                        renderItem={(u) => (
                            <List.Item>
                                <List.Item.Meta
                                    avatar={<Avatar src={u.avatar} />}
                                    title={u.name}
                                    description={u.email}
                                />
                            </List.Item>
                        )}
                    />
                </>
            ),
            closable: true,
            maskClosable: true,
            okText: "Confirm",
            onOk: async () => {
                try {
                    await deleteOrg(org.id);
                    await refreshOrgs();
                } catch (error) {
                    handleError(error);
                }
            },
        });
    };

    const getColumnSearchProps = (
        dataIndex: keyof Organization
    ): TableColumnType<Organization> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => clearFilters?.()} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (open) => {
            if (open) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
    });

    const orgTable = (
        <Table size="small" dataSource={data} loading={loading} rowKey="id">
            <Column
                title="Name"
                sorter={true}
                dataIndex="name"
                key="name"
                {...getColumnSearchProps("name")}
            />
            <Column
                title="Domain"
                sorter={true}
                dataIndex="domain"
                key="domain"
                {...getColumnSearchProps("domain")}
            />
            <Column
                title="Actions"
                width={300}
                key="actions"
                render={(value, row: Organization) => {
                    return (
                        <Space>
                            <Link
                                to={`/admin/orgs/${row.id}/impersonate`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleImpersonate(row);
                                }}
                            >
                                <Button type="primary">Impersonate</Button>
                            </Link>
                            <Button danger onClick={() => handleDelete(row)}>
                                Delete
                            </Button>
                        </Space>
                    );
                }}
            />
        </Table>
    );

    const panel = (
        <Card
            bordered={false}
            title="Organization"
            extra={
                <Button type="primary" size="small" onClick={() => setShowModal(true)}>
                    Create New
                </Button>
            }
        >
            <OrganizationModal
                title="Create New Organization"
                open={showModal}
                onCancel={() => setShowModal(false)}
                onOk={createOrgClick}
            />
            <Row justify={"center"}>
                <Col span={24}>{orgTable}</Col>
            </Row>
        </Card>
    );

    return panel;
};
